import { useTranslation } from "react-i18next";
import { useMedia } from "react-use";
import { UsageStatusDataset } from "../../../types/analytics/UsageStatusDataset";
import {
  getCompletionRate,
  getUsageStatusHeadlineCounts,
} from "../../../helpers/usage/usageStatusChartHelpers";
import { getCompletionBand } from "../../../helpers/usage/usageCompletionBands";
import { useCountUpProgress } from "../../../hooks";

interface UsageJourneyCompletionCardsProps {
  datasets: UsageStatusDataset[];
}

/**
 * Completion headline per selected journey, shown when the filter spans more
 * than one journey. The single-journey view gets the full hero instead, so this
 * deliberately carries only the figure people compare across journeys and
 * leaves the status breakdown to the chart below.
 */
function UsageJourneyCompletionCards({
  datasets,
}: UsageJourneyCompletionCardsProps) {
  const { t } = useTranslation();
  const prefersReducedMotion = useMedia(
    "(prefers-reduced-motion: reduce)",
    false,
  );

  const journeys = datasets.map((dataset) => {
    const { assignedCount, completedCount } =
      getUsageStatusHeadlineCounts(dataset);

    return {
      title: dataset.translateTitle
        ? t(dataset.title, dataset.title)
        : dataset.title,
      assignedCount,
      completedCount,
      completionPercent: getCompletionRate(completedCount, assignedCount),
    };
  });

  // One ramp for every card so the figures count up together rather than racing.
  const countUpProgress = useCountUpProgress(
    journeys
      .map(
        (journey) => `${journey.completionPercent}:${journey.completedCount}`,
      )
      .join("|"),
    !prefersReducedMotion,
  );

  if (journeys.length === 0) {
    return null;
  }

  return (
    <ul
      className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3"
      aria-label={t(
        "Analytics.Widgets.Usage.UsageSummary.Status.CompletionByJourney",
        "Completion by journey",
      )}
    >
      {journeys.map((journey, index) => {
        const band = getCompletionBand(journey.completionPercent);
        const hasAssignments = journey.assignedCount > 0;
        const animatedPercent = Math.round(
          journey.completionPercent * countUpProgress,
        );
        const animatedCompletedCount = Math.round(
          journey.completedCount * countUpProgress,
        );

        return (
          <li
            key={`${journey.title}-${index}`}
            className="rounded-lg border border-gray-200 bg-white p-4"
          >
            <p
              className="truncate text-xs font-semibold uppercase tracking-wide text-gray-500"
              title={journey.title}
            >
              {journey.title}
            </p>

            <div className="mt-1 flex flex-wrap items-baseline gap-x-2">
              {hasAssignments ? (
                <>
                  <span
                    className="flex items-baseline font-bold leading-none tabular-nums"
                    style={{ color: band.textColour }}
                  >
                    <span className="text-3xl">{animatedPercent}</span>
                    <span className="text-lg">%</span>
                  </span>
                  <span
                    className="text-sm font-medium"
                    style={{ color: band.textColour }}
                  >
                    {t(
                      "Analytics.Widgets.Usage.UsageSummary.Status.Completed",
                      "Completed",
                    )}
                  </span>
                </>
              ) : (
                <span
                  aria-hidden
                  className="text-3xl font-bold leading-none text-gray-500"
                >
                  -
                </span>
              )}
            </div>

            <p className="mt-2 text-sm text-gray-600">
              {hasAssignments ? (
                <>
                  <span className="font-medium tabular-nums text-gray-700">
                    {animatedCompletedCount.toLocaleString()}
                  </span>{" "}
                  {t("Analytics.Widgets.Usage.Band.AssignedOf", "of")}{" "}
                  {journey.assignedCount.toLocaleString()}{" "}
                  {t("Analytics.Widgets.Usage.Band.AssignedSuffix", "assigned")}
                </>
              ) : (
                t(
                  "Analytics.Widgets.Usage.UsageSummary.Status.NoneAssigned",
                  "No one is assigned in this view.",
                )
              )}
            </p>
          </li>
        );
      })}
    </ul>
  );
}

export default UsageJourneyCompletionCards;
